import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Menu, X, LogOut, User, Sparkles, BadgeCheck, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/roadmap", label: "Case Roadmap" },
  { to: "/diagnostic", label: "Diagnostic" },
  { to: "/assistant", label: "AI Companion" },
  { to: "/attorneys", label: "Find a Lawyer" },
  { to: "/resources", label: "Resources" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="backdrop-blur-xl bg-parchment-50/90 border-b border-parchment-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[68px]">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <Logo className="h-10 w-10 transition-transform group-hover:scale-105" />
            <span className="font-serif text-2xl font-semibold text-forest-900 leading-none">SentinelGuide</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.slice(1)}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  location.pathname === l.to
                    ? "bg-forest-100 text-forest-900"
                    : "text-ink-700 hover:bg-parchment-100"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                {user.role === "attorney" ? (
                  <Link to="/attorney" data-testid="nav-practice-btn">
                    <Button variant="ghost" className="rounded-full text-forest-800 hover:bg-parchment-100 gap-2"><Briefcase className="h-4 w-4" /> My Practice</Button>
                  </Link>
                ) : (
                  <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="nav-admin-btn">
                    <Button variant="ghost" className="rounded-full text-forest-800 hover:bg-parchment-100">Admin</Button>
                  </Link>
                )}
                {user.is_pro ? (
                  <span data-testid="nav-pro-badge" className="inline-flex items-center gap-1 rounded-full bg-forest-100 text-forest-800 text-xs font-semibold px-3 py-1.5"><BadgeCheck className="h-4 w-4" /> Pro</span>
                ) : (
                  <Link to="/upgrade" data-testid="nav-upgrade-btn">
                    <Button className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-1.5"><Sparkles className="h-4 w-4" /> Case Pass</Button>
                  </Link>
                )}
                <Link to="/dashboard" data-testid="nav-dashboard-btn">
                  <Button variant="ghost" className="rounded-full text-forest-800 hover:bg-parchment-100 gap-2">
                    <User className="h-4 w-4" /> My Vault
                  </Button>
                </Link>
                  </>
                )}
                <Button onClick={doLogout} data-testid="nav-logout-btn" variant="ghost" className="rounded-full text-ink-500 hover:text-terracotta">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn">
                  <Button variant="ghost" className="rounded-full text-forest-800 hover:bg-parchment-100">Log in</Button>
                </Link>
                <Link to="/register" data-testid="nav-register-btn">
                  <Button className="rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50">Get started</Button>
                </Link>
              </>
            )}
          </div>

          <button className="md:hidden p-2 text-forest-900" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-parchment-200 bg-parchment-50 px-4 py-4 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block px-4 py-3 rounded-xl text-ink-700 hover:bg-parchment-100 font-medium">
              {l.label}
            </Link>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)}><Button className="w-full rounded-full bg-forest-800 text-parchment-50">My Vault</Button></Link>
                <Button onClick={() => { setOpen(false); doLogout(); }} variant="outline" className="w-full rounded-full">Log out</Button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)}><Button variant="outline" className="w-full rounded-full">Log in</Button></Link>
                <Link to="/register" onClick={() => setOpen(false)}><Button className="w-full rounded-full bg-forest-800 text-parchment-50">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
